import { sha256 } from 'js-sha256';

import { getQueryClient } from 'app/get-query-client';
import { REFERRALS_ALLOWLISTED_COUNTRY_CODES } from 'constants/restrictedCountries';
import type { FacebookPixelTrackerPayload } from 'lib/facebookPixel';
import { useAuthStore } from 'lib/stores/authStore';
import type { UserVo } from 'models';
import type { ReferralSignupInfoVo } from 'models/referrals';
import { loadLocation } from 'queries/countryCode';
import { referralsSummaryOptions } from 'queries/referralsSummary';
import { getWasReferred } from 'queries/referralsSummary.utils';
import { useReferralStore, type ReferralAttachOutcome } from 'stores/zustand/referralStore';
import { useRegistrationStore } from 'stores/zustand/registrationStore';
import { handleSnackbarError, useSnackbarStore } from 'stores/zustand/snackbarStore';
import { getSignupReferral } from 'transport/domains/referrals';
import analyticsProvider from 'utils/analytics/analyticsProvider';
import { serverErrorSnack } from 'utils/errors';
import { noop } from 'utils/noop';
import { clearReferral, readDurableReferral, resolveReferralForSubmission } from 'utils/referralAttribution';
import { submitPostSignupReferralCode } from 'utils/referralCodeSubmission';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/storage';

const LOGGED_IN_EMAIL_WAIT_TIMEOUT_MS = 15_000;
const POST_SIGNUP_REFERRAL_ELIGIBILITY_WINDOW_MS = 72 * 60 * 60 * 1000;
const MAX_NEW_APPLICANT_ATTACH_ATTEMPTS = 2;

function waitForLoggedInUser(userId: string): Promise<UserVo | undefined> {
    const initial = useAuthStore.getState();
    if (initial.user?.user_id === userId && initial.user.email) {
        return Promise.resolve(initial.user);
    }
    if (initial.userId !== userId) {
        return Promise.resolve(undefined);
    }

    return new Promise((resolve) => {
        let unsubscribe = noop;
        const timeoutId = setTimeout(() => {
            unsubscribe();
            resolve(undefined);
        }, LOGGED_IN_EMAIL_WAIT_TIMEOUT_MS);
        const finish = (user: UserVo | undefined): void => {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve(user);
        };
        unsubscribe = useAuthStore.subscribe((state) => {
            if (state.userId !== userId) {
                finish(undefined);
                return;
            }
            if (state.user?.user_id === userId && state.user.email) {
                finish(state.user);
            }
        });
    });
}

function isWithinPostSignupReferralEligibilityWindow(createdTs: string): boolean {
    const createdAtMs = Date.parse(createdTs);
    if (!Number.isFinite(createdAtMs)) {
        return true;
    }

    return Date.now() - createdAtMs <= POST_SIGNUP_REFERRAL_ELIGIBILITY_WINDOW_MS;
}

async function loadWasReferred(userId: string): Promise<boolean | undefined> {
    try {
        // staleTime 0 forces a fresh fetch — a cached pre-login answer could wrongly skip submission.
        const summary = await getQueryClient().fetchQuery({ ...referralsSummaryOptions(userId), staleTime: 0 });
        return getWasReferred(summary);
    } catch {
        return undefined;
    }
}

export async function submitStoredReferralAfterLogin(
    userId: string,
    getFallbackCountryCode: () => string,
): Promise<void> {
    const referralCode = readDurableReferral();
    if (!referralCode) {
        return;
    }

    const wasReferredPromise = loadWasReferred(userId);
    const user = await waitForLoggedInUser(userId);
    if (!user) {
        return;
    }

    if (!isWithinPostSignupReferralEligibilityWindow(user.created_ts)) {
        clearReferral();
        return;
    }

    const country = user.country || getFallbackCountryCode();
    if (!REFERRALS_ALLOWLISTED_COUNTRY_CODES.includes(country)) {
        return;
    }

    const wasReferred = await wasReferredPromise;
    if (wasReferred === undefined) {
        return;
    }
    if (wasReferred) {
        clearReferral();
        return;
    }

    await submitPostSignupReferralCode(
        user.email,
        referralCode,
        () => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Referral code applied',
                variant: 'positive',
            });
            clearReferral();
        },
        (err) => {
            useSnackbarStore.getState().actions.pushMessage({
                ...serverErrorSnack(err),
                variant: 'negative',
            });
            clearReferral();
        },
    );
}

function recordReferredRegistration(userId: string, referralCode: string): void {
    const eventId = sha256(userId + 'ReferredRegistrations');
    const storageKey = `kalshi_referred_registration_${eventId}`;
    if (getLocalStorageItem(storageKey)) {
        return;
    }

    setLocalStorageItem(storageKey, 'true');
    const trackerPayload: FacebookPixelTrackerPayload = { event_id: eventId, referral_code: referralCode };
    analyticsProvider.sendEvent('ReferredRegistrations', trackerPayload);
}

const activeReferralAttaches = new Map<string, Promise<void>>();

function loadSignupReferralInfo(userId: string): Promise<ReferralSignupInfoVo | undefined> {
    return new Promise((resolve) => {
        getSignupReferral(
            userId,
            (referralSignupInfo) => {
                if (useAuthStore.getState().userId !== userId) {
                    resolve(undefined);
                    return;
                }

                useRegistrationStore.setState({ referralSignupInfo });
                resolve(referralSignupInfo);
            },
            (error) => {
                if (useAuthStore.getState().userId === userId) {
                    if (error.error.code === 'not_found') {
                        useRegistrationStore.setState({ referralSignupInfo: undefined });
                    } else {
                        handleSnackbarError(error);
                    }
                }
                resolve(undefined);
            },
        );
    });
}

async function runReferralAttachForNewApplicant(
    userId: string,
    getFallbackCountryCode: () => string,
): Promise<boolean> {
    const recordOutcome = (outcome: ReferralAttachOutcome): void =>
        useReferralStore.getState().actions.referralAttachOutcomeChanged(userId, outcome);
    recordOutcome('pending');

    const referralSignupInfo = await loadSignupReferralInfo(userId);
    if (referralSignupInfo) {
        recordOutcome('attached');
        if (useAuthStore.getState().userId !== userId) {
            return true;
        }
        recordReferredRegistration(userId, referralSignupInfo.referral_code);
        return true;
    }

    const user = await waitForLoggedInUser(userId);
    if (!user) {
        recordOutcome('skipped');
        return true;
    }

    const detectedCountry =
        user.country ||
        getFallbackCountryCode() ||
        (await new Promise<string>((resolve) => {
            loadLocation(
                (location) => resolve(location.country_iso),
                () => resolve(''),
            );
        }));
    if (useAuthStore.getState().userId !== userId) {
        recordOutcome('skipped');
        return true;
    }
    const country = useAuthStore.getState().user?.country || detectedCountry;
    if (!REFERRALS_ALLOWLISTED_COUNTRY_CODES.includes(country)) {
        recordOutcome('skipped');
        return true;
    }

    const referralState = useReferralStore.getState();
    const referralCode = resolveReferralForSubmission(referralState.referral, referralState.invalidatedReferralCode);
    if (!referralCode) {
        recordOutcome('skipped');
        return true;
    }

    return new Promise<boolean>((resolve) => {
        void submitPostSignupReferralCode(
            user.email,
            referralCode,
            () => {
                recordOutcome('submitted');
                if (useAuthStore.getState().userId === userId) {
                    recordReferredRegistration(userId, referralCode);
                    clearReferral(referralCode);
                }
                resolve(true);
            },
            () => {
                recordOutcome('failed');
                resolve(false);
            },
        )
            .then((outcome) => {
                // The request was never issued because the session ended, so neither
                // callback above will fire and this promise would otherwise never
                // settle — stranding the attach promise and its activeReferralAttaches
                // entry for the life of the tab, which blocks any retry if that user
                // signs in again. `true` because there is nothing to retry against a
                // session that is gone, matching the other session-changed exits here.
                if (outcome === 'session-ended') {
                    recordOutcome('skipped');
                    resolve(true);
                }
            })
            .catch(() => {
                recordOutcome('failed');
                resolve(false);
            });
    });
}

export function attachReferralForNewApplicant(userId: string, getFallbackCountryCode: () => string): Promise<void> {
    const activeAttach = activeReferralAttaches.get(userId);
    if (activeAttach) {
        return activeAttach;
    }

    const pendingAttach = (async () => {
        for (let attempt = 0; attempt < MAX_NEW_APPLICANT_ATTACH_ATTEMPTS; attempt += 1) {
            if (await runReferralAttachForNewApplicant(userId, getFallbackCountryCode)) {
                return;
            }
        }
    })();
    activeReferralAttaches.set(userId, pendingAttach);
    const clearActiveAttach = (): void => {
        if (activeReferralAttaches.get(userId) === pendingAttach) {
            activeReferralAttaches.delete(userId);
        }
    };
    void pendingAttach.then(clearActiveAttach, clearActiveAttach);
    return pendingAttach;
}
